import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAdTypeDto, IdPlacementDto } from '@/services/ws-point-of-sale/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { usePosAdTypeOptions } from '../hooks/usePosAdTypeOptions';

interface PosAdTypeFilterProps {
  placementId: IdPlacementDto | undefined;
  value: Option<IdAdTypeDto> | null;
  onChange: (value: Option<IdAdTypeDto> | null) => void;
  label?: string;
  dataTestId?: string;
}

export const PosAdTypeFilter: FC<PosAdTypeFilterProps> = memo(function PosAdTypeFilter({
  placementId,
  value,
  onChange,
  label,
  dataTestId,
}) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading, total, searchFilter, setSearchFilter } = usePosAdTypeOptions({ placementId });

  const handleChange = useCallbackRef((newValue: Option<IdAdTypeDto> | null) => {
    onChange(newValue);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: searchFilter, setFilter: setSearchFilter, total }),
    [searchFilter, setSearchFilter, total],
  );

  return (
    <Filter<IdAdTypeDto>
      label={label || t('common:AD_TYPE')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={serverSideFiltering}
      options={options}
      dataTestId={dataTestId || 'pos-ad-type-dropdown'}
    />
  );
});
